import { BottomDrawer } from '@landtrustinc/design-system';
import React from 'react';

import { useBuckChatDrawer } from '../../hooks/useBuckChatDrawer';
import type {
  BuckChatConfig,
  BuckChatProps,
  ChatMessage,
  CurrentUser,
  LtPlusCta,
} from '../../types/index';
import LtPlusDrawer from '../LtPlusDrawer/LtPlusDrawer';
import SignupDrawer from '../SignupDrawer/SignupDrawer';
import { useDrawerManagerFlow } from './hooks/useDrawerManagerFlow';

type DrawerManagerProps = {
  config: BuckChatConfig;
  listingId?: string;
  packageId?: string;
  currentUser?: CurrentUser;
  onAnalyticsEvent?: BuckChatProps['onAnalyticsEvent'];
  onAuthSuccess?: BuckChatProps['onAuthSuccess'];
  onLtPlusSuccess?: BuckChatProps['onLtPlusSuccess'];
  updateMessage: (messageId: string, updates: Partial<ChatMessage>) => void;
  children: (callbacks: {
    onSignupClick: (msg?: ChatMessage) => void;
    onLtPlusCtaClick: (ltPlusCta: LtPlusCta, messageId?: string) => void;
  }) => React.ReactNode;
};

const DrawerManager: React.FC<DrawerManagerProps> = ({
  config,
  listingId,
  packageId,
  currentUser,
  onAnalyticsEvent,
  onAuthSuccess,
  onLtPlusSuccess,
  updateMessage,
  children,
}) => {
  const drawer = useBuckChatDrawer();
  const {
    handleSignupClick,
    handleAuthSuccess,
    handleSignupAbandoned,
    handleLtPlusCtaClick,
    handleLtPlusDrawerClose,
    handleLtPlusSuccess,
    handleLtPlusSignInClick,
  } = useDrawerManagerFlow({
    drawer,
    listingId,
    packageId,
    currentUser,
    onAnalyticsEvent,
    onAuthSuccess,
    onLtPlusSuccess,
    updateMessage,
  });

  return (
    <>
      {children({ onSignupClick: handleSignupClick, onLtPlusCtaClick: handleLtPlusCtaClick })}

      {/* Single Bottom Drawer with dynamic content */}
      <BottomDrawer
        {...drawer.drawerProps}
        onClose={drawer.drawerState.type === 'ltplus' ? handleLtPlusDrawerClose : drawer.close}
      >
        {drawer.drawerState.type === 'signup' && (
          <SignupDrawer
            config={config}
            context={{ listingId, packageId, ...drawer.drawerState.signupContext }}
            onSuccess={handleAuthSuccess}
            variant={drawer.drawerState.signupVariant}
            onAbandoned={handleSignupAbandoned}
            onAnalyticsEvent={onAnalyticsEvent}
          />
        )}

        {drawer.drawerState.type === 'ltplus' && config.stripe && (
          <LtPlusDrawer
            ltPlusCta={drawer.drawerState.ltPlusCta || null}
            stripeConfig={config.stripe}
            buckChatConfig={config}
            currentUser={currentUser}
            initialStep={drawer.drawerState.ltPlusInitialStep}
            onClose={handleLtPlusSuccess}
            onSignInRequired={handleLtPlusSignInClick}
            onAnalyticsEvent={onAnalyticsEvent}
          />
        )}
      </BottomDrawer>
    </>
  );
};

export default DrawerManager;
